/**
 * Excel Translations API Module
 * Handles translations of phrases from Excel files with moderator proposals and admin validation
 */

import { apiClient } from "./api-client";

// ============================================
// Types
// ============================================

export interface ExcelPhrase {
  phraseId: string;
  frenchPhrase: string;
  localPhrase?: string;
  source?: string;
  domain?: string;
}

export type ExcelFileLanguageStatus = 'available' | 'pending' | 'translated';

export interface ExcelFile {
  excelFileId: number;
  fileName: string;
  lang: string;
  totalPhrases: number;
  emptyPhrasesCount: number;
  translationStatus: Record<string, ExcelFileLanguageStatus>;
  createdAt: string;
  phrases: ExcelPhrase[];
  phrasesTotal: number;
  phrasesPage: number;
  phrasesLimit: number;
}

export interface GetExcelFilesResponse {
  files: ExcelFile[];
  total: number;
  page: number;
  limit: number;
}

export type ProposalStatus = 'pending' | 'approved' | 'rejected';

// Why a phrase was left out of an approval: it was translated or changed in the meantime.
export interface SkippedPhrase {
  phraseId: string;
  reason: 'not_found' | 'already_translated' | 'source_changed' | 'invalid_content' | string;
}

export interface ProposedPhrase {
  phraseId: string;
  originalFr?: string;
  proposedLocal: string;
}

// Shape returned by GET /words/translations/proposals/excel (word.service getExcelTranslationProposals).
export interface TranslationProposal {
  proposalId: string;
  excelFileId: number;
  fileName: string;
  language: string;
  // 'processing': another admin is validating it right now.
  status: ProposalStatus | 'processing';
  totalPhrases: number;
  moderator: string;
  createdAt: string;
  approvedAt?: string;
  // Added by newer backends; older ones only return the counters above.
  phrases?: ProposedPhrase[];
  skippedPhrases?: SkippedPhrase[];
}

export interface TranslationPhraseDto {
  phraseId: string;
  proposedLocal: string;
}

export interface ProposeExcelTranslationDto {
  excelFileId: number;
  language: string;
  translations: TranslationPhraseDto[];
}

export interface ExcelTranslationProposalResponse {
  message?: string;
  excelFileId?: number;
  language?: string;
  proposalsCreated?: number;
  [key: string]: unknown;
}

export interface ValidateProposalDto {
  status: 'approved' | 'rejected';
  adminNotes?: string;
}

export interface ValidateProposalResponse {
  proposalId: string;
  excelFileId: number;
  language: string;
  status: 'approved' | 'rejected';
  totalPhrases: number;
  updatedCount: number;
  adminNotes?: string;
  validatedAt: string;
  message: string;
  skippedPhrases?: SkippedPhrase[];
}

export interface PaginatedProposalsResponse {
  proposals: TranslationProposal[];
  total: number;
  page: number;
  limit: number;
}

// ============================================
// Excel Translations API
// ============================================

export const excelTranslationsApi = {
  // Get Excel files with empty phrases in a specific language
  getExcelFiles: async (
    language: string,
    sectorId?: string,
    page: number = 1,
    limit: number = 20,
    phrasePage: number = 1,
    phraseLimit: number = 20,
    signal?: AbortSignal,
  ): Promise<GetExcelFilesResponse> => {
    const params: Record<string, string | number> = { language, page, limit, phrasePage, phraseLimit };
    if (sectorId) params.sectorId = sectorId;

    const { data } = await apiClient.get<GetExcelFilesResponse>(
      '/words/translations/excel-files',
      { params, signal }
    );
    return data;
  },

  // Submit a batch of phrase translations for an Excel file in one language
  proposeExcelTranslation: async (
    payload: ProposeExcelTranslationDto,
  ): Promise<ExcelTranslationProposalResponse> => {
    const { data } = await apiClient.post<ExcelTranslationProposalResponse>(
      '/words/translations/excel/propose',
      payload
    );
    return data;
  },

  // Get all translation proposals (for admin validation)
  getProposals: async (
    language?: string,
    status?: ProposalStatus,
    page: number = 1,
    limit: number = 20,
    signal?: AbortSignal,
  ): Promise<PaginatedProposalsResponse> => {
    const params: Record<string, string | number> = { page, limit };
    if (language) params.language = language;
    if (status) params.status = status;

    const { data } = await apiClient.get<PaginatedProposalsResponse>(
      '/words/translations/proposals/excel',
      { params, signal }
    );
    return data;
  },

  // Validate a translation proposal (admin only)
  validateProposal: async (
    proposalId: string,
    payload: ValidateProposalDto,
  ): Promise<ValidateProposalResponse> => {
    const { data } = await apiClient.put<ValidateProposalResponse>(
      `/words/translations/proposals/excel/${proposalId}/validate`,
      payload
    );
    return data;
  },
};
